/**
 * Il giro dell'accoppiamento: chiedi un codice, mostralo, interroga finché arriva.
 *
 * Sta qui e non nella schermata perché la schermata non deve conoscere né la rete né i timer:
 * riceve uno stato e disegna. Qui invece ci sono le due cose che si sbagliano in silenzio —
 * **quando smettere di interrogare** e **quando il codice è morto** — e la regola che le decide sta
 * nel dominio (`reactToPoll`), non in questo file.
 *
 * ## Il tempo si misura monotono
 *
 * Non con `Date.now()`. Un televisore può correggere l'orologio via rete proprio mentre il codice è
 * a schermo — e spesso lo fa appena dopo l'accensione, che è esattamente questo momento — e un
 * salto di ore farebbe scadere all'istante una richiesta appena nata, o la terrebbe viva per sempre.
 * `performance.now()` non salta.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  POLL_INTERVAL_MS,
  pairingExpiryMs,
  pairingIsAlive,
  pairingRemainingSeconds,
  thrownDetail,
  reactToPoll,
  type Credentials,
  type GatewayError,
} from '@player/domain';
import type { PairingPort } from '@player/ports';
import { PairingScreen, PhonePairingScreen, type PairingStatus } from '@player/ui';
import type { FocusPort } from '@player/ports';
import { usePhoneLayout } from './usePhoneLayout.js';

export interface PairingHostProps {
  readonly focusPort: FocusPort;
  readonly deviceId: string;
  readonly appVersion: string;
  readonly pairing: PairingPort;
  /** Millisecondi monotoni. Non è l'ora del giorno, ed è il punto. */
  readonly elapsedMs: () => number;
  /**
   * Perché siamo tornati qui, quando ci siamo tornati.
   *
   * Non è un errore dell'accoppiamento: è il rifiuto della linea che c'era prima — scaduta,
   * sospesa, gettone rigenerato. Si mostra accanto al codice nuovo, perché senza, chi guarda vede
   * ricomparire la schermata di attivazione senza sapere di cosa.
   */
  readonly notice: GatewayError | null;
  /** Vero mentre chi ospita sta provando le credenziali appena arrivate. */
  readonly entering: boolean;
  readonly onPaired: (credentials: Credentials) => void;
}

/** Ogni quanto si ridisegna il conto alla rovescia. */
const TICK_MS = 1_000;

export function PairingHost({
  focusPort,
  deviceId,
  appVersion,
  pairing,
  elapsedMs,
  notice,
  entering,
  onPaired,
}: PairingHostProps): ReactNode {
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState<PairingStatus>({ kind: 'requesting' });
  const [expiresAt, setExpiresAt] = useState<number | null>(null);

  /*
    Sta **sopra** ogni ritorno condizionale, con gli altri hook.

    Messo accanto al ramo che lo usa sarebbe un hook dentro un ramo, e React smetterebbe di
    riconoscere la lista: il difetto non si presenta come un errore qui, ma come stati di un altro
    componente che ricompaiono nel posto sbagliato.
  */
  const phone = usePhoneLayout();

  /*
    Le richiamate stanno in riferimenti e **non** fra le dipendenze dell'effetto.

    Se chi ospita le ricrea a ogni disegno — ed è quel che succede scrivendole in linea — l'effetto
    ripartirebbe di continuo: un codice nuovo ogni pochi millisecondi, il limite di frequenza del
    servizio esaurito in un attimo, e uno schermo che sfarfalla senza che nessuno capisca perché.
  */
  const onPairedRef = useRef(onPaired);
  useEffect(() => {
    onPairedRef.current = onPaired;
  }, [onPaired]);

  const elapsedRef = useRef(elapsedMs);
  useEffect(() => {
    elapsedRef.current = elapsedMs;
  }, [elapsedMs]);

  const renew = useCallback(() => setAttempt((previous) => previous + 1), []);

  // ── Chiedi un codice, poi interroga ────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const now = (): number => elapsedRef.current();

    const poll = async (code: string, expiry: number): Promise<void> => {
      if (cancelled) return;

      // A zero **non si interroga più**: un codice morto consuma il limite di frequenza per
      // niente, e dal divano è indistinguibile da un guasto.
      if (!pairingIsAlive(expiry, now())) {
        setStatus({ kind: 'expired' });
        return;
      }

      const outcome = await pairing
        .poll(code)
        .catch(() => ({ kind: 'offline' as const }));
      if (cancelled) return;

      const reaction = reactToPoll(outcome);
      if (reaction.step === 'enter' && outcome.kind === 'ready') {
        onPairedRef.current({
          kind: 'token',
          host: outcome.host,
          token: outcome.token,
          serviceName: outcome.serviceName,
        });
        return;
      }
      if (reaction.step === 'renew') {
        // Il codice è morto prima della scadenza — consegnato a qualcun altro, o il servizio
        // riavviato. Se ne chiede uno nuovo invece di ritentare quello vecchio, che non tornerà.
        renew();
        return;
      }
      timer = setTimeout(() => void poll(code, expiry), reaction.delayMs);
    };

    void (async () => {
      setStatus({ kind: 'requesting' });
      setExpiresAt(null);
      try {
        const request = await pairing.create();
        if (cancelled) return;
        const expiry = pairingExpiryMs(request.expiresInSeconds, now());
        setExpiresAt(expiry);
        setStatus({
          kind: 'showing',
          code: request.code,
          remainingSeconds: pairingRemainingSeconds(expiry, now()),
        });
        timer = setTimeout(() => void poll(request.code, expiry), POLL_INTERVAL_MS);
      } catch (error) {
        if (cancelled) return;
        setStatus({
          kind: 'failed',
          error:
            thrownDetail(error, Date.now()),
        });
      }
    })();

    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [attempt, pairing, renew]);

  // ── Il conto alla rovescia ─────────────────────────────────────────────────
  useEffect(() => {
    if (expiresAt === null) return undefined;
    const id = setInterval(() => {
      const remaining = pairingRemainingSeconds(expiresAt, elapsedRef.current());
      setStatus((previous) => {
        if (previous.kind !== 'showing') return previous;
        // La scadenza la dichiara chi conta, non la prossima interrogazione: aspettare quella
        // lascerebbe fino a cinque secondi di conto fermo a zero con il codice ancora a schermo.
        return remaining > 0 ? { ...previous, remainingSeconds: remaining } : { kind: 'expired' };
      });
    }, TICK_MS);
    return () => clearInterval(id);
  }, [expiresAt]);

  /*
    Due disegni della stessa schermata, scelti dalla **larghezza** e non dal sistema operativo.

    Lo stesso APK Android gira su un televisore e su un telefono, e `detectPlatform` dice `androidTv`
    anche a un telefono: fidarsi di quello darebbe l'interfaccia da telecomando a chi ha un dito.
    La domanda giusta è quanto è largo lo schermo, ed è `usesPhoneLayout` a rispondere.

    Si rivaluta a ogni disegno perché la larghezza cambia mentre l'app è aperta: basta ruotare il
    telefono.
  */
  if (phone) {
    return (
      <PhonePairingScreen
        deviceId={deviceId}
        appVersion={appVersion}
        status={status}
        notice={notice}
        entering={entering}
        onRenew={renew}
      />
    );
  }

  return (
    <PairingScreen
      focusPort={focusPort}
      deviceId={deviceId}
      appVersion={appVersion}
      status={status}
      notice={notice}
      entering={entering}
      onRenew={renew}
    />
  );
}
